import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import BeaconChartWithLegend, {
  BeaconChartWithLegendProps
} from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import SplineChart, {
  SplineChartOptionsOverride,
  SplineChartProps
} from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import { SplineChartV2 } from 'src/components/BeaconV2Components/SplineChartV2';
import { useAppSelector } from 'src/utils/Hooks';
import SplineChartLoading, {
  SplineChartLoadingProps
} from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
import { getSearchAppName, getQueryParamValue } from 'src/utils/app';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import { useMetricByWeekId, UseMetricByWeekIdArgs } from 'src/serverProxy/useMetricByWeekId';
import { useMetricByWeekIdV2 } from 'src/components/BeaconV2Components/SplineChartV2';
import { isAdSpendMetricId } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdvertisingConstants';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import convertLineChartSeriesToDelimitedData, {
  Series
} from 'src/components/Charts/GenericChart/SeriesConverters/lineChart';
import saveAs from 'file-saver';
import { weekIdToTimestamp } from 'src/utils/dateUtils';

export interface BeaconMetricSplineChartProps
  extends Pick<
      BeaconChartWithLegendProps,
      'singleDropdownProps' | 'buildDropdownMenuOptions' | 'dropdownOptionHandler'
    >,
    UseMetricByWeekIdArgs {
  indexName: string;
  fieldName: string;
  title?: string;
  /**
   * Hides the title and category subtitle if false
   */
  showTitleSummary?: boolean;
  /**
   * Optional overrides to the default Highcharts chart options
   */
  chartOptionsOverride?: SplineChartOptionsOverride;
  /**
   * Used for indexing overrides for the index field
   */
  entityType?: string;
  splineChartLoadingProps?: SplineChartLoadingProps;
  beaconChartWithLegendOverride?: (
    props: Omit<BeaconChartWithLegendProps, 'children'>,
    metricByValues: ReturnType<typeof useMetricByWeekId>
  ) => Omit<BeaconChartWithLegendProps, 'children'>;
  splineChartProps?: Partial<SplineChartProps>;
  byDayId?: boolean;
  /**
   * When true, comparison data uses the same time period as primary (e.g., compare mode
   * comparing different metrics or groups). Passed to useMetricByWeekIdV2 for proper alignment.
   */
  isComparisonSameTimePeriod?: boolean;
}

/**
 * Given an index and a field name, displays a weekly spline chart.
 * Uses SplineChartV2 by default, falls back to SplineChart for byDayId (Advertising).
 */
const BeaconMetricSplineChart = ({
  fieldName,
  indexName,
  title,
  showTitleSummary = true,
  chartOptionsOverride,
  entityType,
  primaryAggregationBuilder,
  secondaryAggregationBuilder,
  splineChartLoadingProps,
  metricTypeDisplay,
  aggregationFunctionForTotal,
  requestBuilder,
  comparisonFieldName,
  comparisonIndexName,
  isComparisonDataCustom,
  buildDropdownMenuOptions,
  dropdownOptionHandler,
  beaconChartWithLegendOverride,
  splineChartProps,
  byDayId,
  customFilterComparisonForTotal,
  isComparisonSameTimePeriod,
  ...chartWithLegendProps
}: BeaconMetricSplineChartProps) => {
  // Use V1 hook for byDayId (Advertising daily data), V2 for weekly data
  const metricByValuesV1 = useMetricByWeekId({
    fieldName,
    indexName,
    entityType,
    primaryAggregationBuilder,
    secondaryAggregationBuilder,
    metricTypeDisplay,
    aggregationFunctionForTotal,
    requestBuilder,
    comparisonFieldName,
    comparisonIndexName,
    isComparisonDataCustom,
    customFilterComparisonForTotal,
    byDayId,
    // Only fetch if using V1 (byDayId mode)
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: !!byDayId
    }
  });

  const metricByValuesV2 = useMetricByWeekIdV2({
    fieldName,
    indexName,
    entityType,
    primaryAggregationBuilder,
    secondaryAggregationBuilder,
    metricTypeOverride: metricTypeDisplay,
    aggregationFunctionForTotal,
    requestBuilder,
    comparisonFieldName,
    comparisonIndexName,
    isComparisonSameTimePeriod,
    // Only fetch if using V2 (weekly mode)
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: !byDayId
    }
  });

  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);

  const field = useMemo(
    () => INDEX_FIELDS.getField(getSearchAppName(), indexName, fieldName, entityType),
    [indexName, fieldName, entityType]
  );

  const useLatestWeek = field && field.timePeriodAggregationFunctionType === 'lastValue';
  const useDailyData = getQueryParamValue('tab') === 'advertising';
  const titleToUse = title || field.displayName;

  // Determine which values to use based on byDayId
  const isLoading = byDayId ? metricByValuesV1.isLoading : metricByValuesV2.isLoading;

  // For V1 (byDayId mode)
  const v1MainMetric = useLatestWeek
    ? metricByValuesV1.formattedLastPrimaryValue
    : metricByValuesV1.formattedPrimaryTotal;
  const v1SecondaryMetric = useLatestWeek
    ? metricByValuesV1.formattedLastSecondaryValue
    : metricByValuesV1.formattedSecondaryTotal;
  const v1RawChange = useLatestWeek ? metricByValuesV1.percentChangeLastValues : metricByValuesV1.percentChange;
  const v1Change = useLatestWeek
    ? metricByValuesV1.formattedPercentChangeLastValues
    : metricByValuesV1.formattedPercentChange;

  // For V2 (weekly mode)
  const v2MainMetric = useLatestWeek
    ? metricByValuesV2.formattedLastPrimaryValue
    : metricByValuesV2.formattedPrimaryTotal;
  const v2SecondaryMetric = useLatestWeek
    ? metricByValuesV2.formattedLastComparisonValue
    : metricByValuesV2.formattedComparisonTotal;
  const v2RawChange = useLatestWeek ? metricByValuesV2.lastValuePercentChange : metricByValuesV2.percentChange;
  const v2Change = useLatestWeek
    ? metricByValuesV2.formattedLastValuePercentChange
    : metricByValuesV2.formattedPercentChange;

  // Use appropriate values based on mode
  const mainMetric = byDayId ? v1MainMetric : v2MainMetric;
  const secondaryMetric = byDayId ? v1SecondaryMetric : v2SecondaryMetric;
  const rawChange = byDayId ? v1RawChange : v2RawChange;
  const change = byDayId ? v1Change : v2Change;

  /**
   * Convert series data to CSV format for export
   */
  const convertSeriesToDelimitedData = () => {
    try {
      if (byDayId) {
        const { primaryData, secondaryData } = metricByValuesV1;
        const csvExportedPrimaryData =
          comparisonTimePeriod.id === 'prior-period' && useDailyData && primaryData.length > 1
            ? primaryData.slice(1)
            : primaryData;

        const formatDate = (ts: number) => {
          const d = new Date(ts);
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return `${months[d.getUTCMonth()]} ${String(d.getUTCDate()).padStart(2, '0')}, ${d.getUTCFullYear()}`;
        };

        const primaryMap = new Map<number, number>();
        const comparisonMap = new Map<number, number>();
        csvExportedPrimaryData.forEach((row) => primaryMap.set(row[0], row[1]));
        secondaryData.forEach((row) => comparisonMap.set(row[0], row[1]));

        const allTimestamps = [...new Set([...primaryMap.keys(), ...comparisonMap.keys()])].sort((a, b) => a - b);

        const header = `"Date","${comparisonTimePeriod.displayName}","${mainTimePeriod.displayName}"`;
        const rows = allTimestamps.map((ts) => {
          const date = formatDate(ts);
          const compVal = comparisonMap.has(ts) ? comparisonMap.get(ts) : '';
          const primVal = primaryMap.has(ts) ? primaryMap.get(ts) : '';
          return `"${date}",${compVal},${primVal}`;
        });

        const csvContent = [header, ...rows].join('\n') + '\n';
        const blob = new Blob([csvContent], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, `${titleToUse}.csv`);
        return Promise.resolve();
      }

      const { primaryData, comparisonData } = metricByValuesV2;
      const isPriorYear = comparisonTimePeriod.id !== 'prior-period';
      const primaryDataForExport = primaryData.map((point) => ({
        x: weekIdToTimestamp(point.weekId) || 0,
        y: point.value ?? 0
      }));
      const secondaryDataForExport = comparisonData
        .map((point, index) => {
          if (!isPriorYear) {
            return { x: weekIdToTimestamp(point.weekId) || 0, y: point.value ?? 0 };
          }
          const alignedX = primaryData[index]
            ? weekIdToTimestamp(primaryData[index].weekId) || 0
            : weekIdToTimestamp(point.weekId + 100) || 0;
          return { x: alignedX, y: point.value ?? 0 };
        })
        .filter((d) => d.x > 0);

      const data = convertLineChartSeriesToDelimitedData(
        [
          {
            type: 'spline',
            name: comparisonTimePeriod.displayName,
            data: secondaryDataForExport.map((d) => ({ ...d, marker: { enabled: true } }))
          } as unknown as Series,
          {
            type: 'spline',
            name: mainTimePeriod.displayName,
            data: primaryDataForExport.map((d) => ({ ...d, marker: { enabled: true } }))
          } as unknown as Series
        ],
        comparisonTimePeriod.id === 'prior-period'
      );

      const blob = new Blob([data], { type: 'text/plain;charset=utf-8' });
      saveAs(blob, `${titleToUse}.csv`);
    } catch (e) {
      console.error('error: ', e);
    }

    return Promise.resolve();
  };

  const getBeaconChartWithLegendProps = () => {
    const defaultProps: Omit<BeaconChartWithLegendProps, 'children'> = {
      title: titleToUse,
      primaryLegendProps: {
        displayName: mainTimePeriod.displayName,
        metric: mainMetric,
        change,
        direction: getDirection(rawChange),
        ...(byDayId && isAdSpendMetricId(fieldName) && { changeColorOverride: 'secondary' as const })
      },
      comparisonLegendProps: {
        displayName: comparisonTimePeriod.displayName,
        metric: secondaryMetric,
        direction: getDirection(rawChange)
      },
      loading: isLoading,
      loadingComponent: <SplineChartLoading {...splineChartLoadingProps} />,
      showTitleSummary,
      buildDropdownMenuOptions,
      dropdownOptionHandler,
      convertSeriesToDelimitedData,
      ...chartWithLegendProps
    };

    if (beaconChartWithLegendOverride) {
      // Pass the appropriate metric values based on mode (V1 for byDayId, V2 for weekly)
      // For V2 mode, convert to V1-compatible format for backwards compatibility
      // Type assertion is used because V2 response structure is compatible at runtime
      // but has different TypeScript types
      const metricValues = byDayId
        ? metricByValuesV1
        : ({
            ...metricByValuesV1,
            primaryTotal: metricByValuesV2.primaryTotal,
            secondaryTotal: metricByValuesV2.comparisonTotal,
            formattedPrimaryTotal: metricByValuesV2.formattedPrimaryTotal,
            formattedSecondaryTotal: metricByValuesV2.formattedComparisonTotal,
            percentChange: metricByValuesV2.percentChange,
            formattedPercentChange: metricByValuesV2.formattedPercentChange,
            isLoading: metricByValuesV2.isLoading,
            response: metricByValuesV2.response
          } as typeof metricByValuesV1);
      return beaconChartWithLegendOverride(defaultProps, metricValues);
    }

    return defaultProps;
  };

  // Render V1 SplineChart for byDayId (Advertising), V2 for weekly data
  const renderChart = () => {
    if (byDayId) {
      // Use original SplineChart for daily data (Advertising)
      return (
        <SplineChart
          byDayId={byDayId}
          primaryData={metricByValuesV1.primaryData}
          secondaryData={metricByValuesV1.secondaryData}
          metricType={metricTypeDisplay || field.metricType}
          chartOptionsOverride={chartOptionsOverride}
          {...splineChartProps}
        />
      );
    }

    // Use SplineChartV2 for weekly data
    return (
      <SplineChartV2
        primarySeries={metricByValuesV2.primarySeries}
        comparisonSeries={metricByValuesV2.comparisonSeries}
        metricType={metricTypeDisplay || field.metricType}
        chartOptionsOverride={chartOptionsOverride as Partial<Highcharts.Options>}
      />
    );
  };

  return <BeaconChartWithLegend {...getBeaconChartWithLegendProps()}>{renderChart()}</BeaconChartWithLegend>;
};

export default BeaconMetricSplineChart;
